import axios from '../axios';
import {
  ADD_CONTENT_TO_AD_LIBRARY,
  ADD_TOOL_TO_LIBRARY,
  GET_AD_LIBRARY_CONTENT,
} from '../ApiUrl';

function normalizeBranchName(branchName) {
  return String(branchName ?? '').trim().toLowerCase();
}

export const AD_LIBRARY_CONTENT_TYPE = 'ad_library';
export const TOOL_LIBRARY_CONTENT_TYPE = 'tool_library';

export function normalizeContentType(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === TOOL_LIBRARY_CONTENT_TYPE) return TOOL_LIBRARY_CONTENT_TYPE;
  return AD_LIBRARY_CONTENT_TYPE;
}

function mapToolLibraryApiItem(item) {
  return {
    id: String(item?._id ?? ''),
    contentType: TOOL_LIBRARY_CONTENT_TYPE,
    toolName: String(item?.tool_name ?? '').trim() || 'Untitled',
    shortDescription: String(item?.short_description ?? '').trim(),
    websiteUrl: String(item?.website_url ?? '').trim(),
    category: String(item?.category ?? '').trim(),
    status: item?.status === true,
    featured: item?.featured === true,
    createdBy: String(item?.created_by ?? '').trim(),
    managingBranchName: String(item?.managing_branch_name ?? '').trim(),
    notes: String(item?.notes ?? '').trim(),
    createdAt: item?.createdAt ?? null,
    updatedAt: item?.updatedAt ?? null,
    raw: item,
  };
}

function mapAdContentApiItem(item) {
  return {
    id: String(item?._id ?? item?.ad_id ?? ''),
    contentType: AD_LIBRARY_CONTENT_TYPE,
    title: String(item?.ad_title ?? '').trim() || 'Untitled',
    type: item?.ad_type === 'video' ? 'video' : 'image',
    category: String(item?.category ?? '').trim().toLowerCase(),
    accessUrl: String(item?.access_url ?? '').trim(),
    thumbnailUrl: item?.thumbnail_url ? String(item.thumbnail_url).trim() : null,
    status: String(item?.status ?? 'draft'),
    tags: Array.isArray(item?.tags) ? item.tags : [],
    goodToUseOn: Array.isArray(item?.good_to_use_on) ? item.good_to_use_on : [],
    uploadedAt: item?.uploaded_at ?? item?.createdAt ?? null,
    raw: item,
  };
}

export function mapAdLibraryApiItem(item) {
  const contentType = normalizeContentType(item?.content_type);
  if (contentType === TOOL_LIBRARY_CONTENT_TYPE || item?.tool_name != null) {
    return mapToolLibraryApiItem(item);
  }
  return mapAdContentApiItem(item);
}

export function parseGetAdLibraryContentResponse(res) {
  if (res?.hasError) {
    const message =
      typeof res?.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'Could not load ad library content.';
    return { ok: false, items: [], count: 0, message };
  }

  const body = res?.data;
  if (body?.success === false) {
    return {
      ok: false,
      items: [],
      count: body?.count ?? 0,
      message: body?.message || 'Request failed.',
    };
  }

  const items = Array.isArray(body?.data) ? body.data.map(mapAdLibraryApiItem) : [];
  return {
    ok: true,
    items,
    count: body?.count ?? items.length,
    message: body?.message || '',
  };
}

export function parseAddToolToLibraryResponse(res) {
  if (res?.hasError) {
    const message =
      typeof res?.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'Could not add tool to library.';
    return { ok: false, item: null, message };
  }

  const body = res?.data;
  if (body?.success === false) {
    return {
      ok: false,
      item: null,
      message: body?.message || 'Request failed.',
    };
  }

  const item = body?.data ? mapToolLibraryApiItem(body.data) : null;
  return {
    ok: true,
    item,
    message: body?.customMessage || body?.message || 'Tool added successfully.',
  };
}

export function parseAddContentToAdLibraryResponse(res) {
  if (res?.hasError) {
    const message =
      typeof res?.message === 'string' && res.message.trim()
        ? res.message.trim()
        : 'Could not add ad library content.';
    return { ok: false, item: null, message };
  }

  const body = res?.data;
  if (body?.success === false) {
    return {
      ok: false,
      item: null,
      message: body?.message || 'Request failed.',
    };
  }

  const item = body?.data ? mapAdLibraryApiItem(body.data) : null;
  return {
    ok: true,
    item,
    message: body?.customMessage || body?.message || 'Content added successfully.',
  };
}

/**
 * POST /GetAdLibraryContent
 * Body: { branchName }
 */
export const getAdLibraryContentAPI = async ({ branchName } = {}) => {
  const bn = normalizeBranchName(branchName);
  if (!bn) {
    return { hasError: true, message: 'branchName is required.' };
  }

  try {
    const response = await axios.post(GET_AD_LIBRARY_CONTENT, { branchName: bn });
    return response;
  } catch (error) {
    console.log('getAdLibraryContentAPI catch', error);
    return { hasError: true, error };
  }
};

/**
 * POST /AddContentToAdLibrary
 */
export const addContentToAdLibraryAPI = async ({
  branchName,
  ad_title,
  ad_type,
  category,
  access_url,
  good_to_use_on,
  thumbnail_url,
  status,
  tags,
  uploaded_by_userId,
  uploaded_by_user_type,
  uploaded_by_user_name,
} = {}) => {
  const bn = normalizeBranchName(branchName);
  if (!bn) {
    return { hasError: true, message: 'branchName is required.' };
  }

  const body = {
    branchName: bn,
    ad_title: String(ad_title ?? '').trim(),
    ad_type: String(ad_type ?? '').trim().toLowerCase(),
    category: String(category ?? '').trim().toLowerCase(),
  };

  if (access_url != null && String(access_url).trim() !== '') {
    body.access_url = String(access_url).trim();
  }

  if (Array.isArray(good_to_use_on)) body.good_to_use_on = good_to_use_on;
  if (thumbnail_url != null && String(thumbnail_url).trim() !== '') {
    body.thumbnail_url = String(thumbnail_url).trim();
  }
  if (status != null && String(status).trim() !== '') {
    body.status = String(status).trim().toLowerCase();
  }
  if (Array.isArray(tags)) body.tags = tags;
  if (uploaded_by_userId != null) body.uploaded_by_userId = uploaded_by_userId;
  if (uploaded_by_user_type != null && String(uploaded_by_user_type).trim() !== '') {
    body.uploaded_by_user_type = String(uploaded_by_user_type).trim();
  }
  if (uploaded_by_user_name != null && String(uploaded_by_user_name).trim() !== '') {
    body.uploaded_by_user_name = String(uploaded_by_user_name).trim();
  }

  try {
    const response = await axios.post(ADD_CONTENT_TO_AD_LIBRARY, body);
    return response;
  } catch (error) {
    console.log('addContentToAdLibraryAPI catch', error);
    return { hasError: true, error };
  }
};

/**
 * POST /AddToolToLibrary
 */
export const addToolToLibraryAPI = async ({
  tool_name,
  website_url,
  short_description,
  category,
  status,
  featured,
  managing_branch_name,
  notes,
  uploaded_by_userId,
  uploaded_by_user_type,
  uploaded_by_user_name,
} = {}) => {
  const body = {
    tool_name: String(tool_name ?? '').trim(),
    website_url: String(website_url ?? '').trim(),
  };

  if (short_description != null && String(short_description).trim() !== '') {
    body.short_description = String(short_description).trim();
  }
  if (category != null && String(category).trim() !== '') {
    body.category = String(category).trim();
  }
  if (typeof status === 'boolean') body.status = status;
  if (typeof featured === 'boolean') body.featured = featured;
  if (managing_branch_name != null && String(managing_branch_name).trim() !== '') {
    body.managing_branch_name = normalizeBranchName(managing_branch_name);
  }
  if (notes != null && String(notes).trim() !== '') {
    body.notes = String(notes).trim();
  }
  if (uploaded_by_userId != null) body.uploaded_by_userId = uploaded_by_userId;
  if (uploaded_by_user_type != null && String(uploaded_by_user_type).trim() !== '') {
    body.uploaded_by_user_type = String(uploaded_by_user_type).trim();
  }
  if (uploaded_by_user_name != null && String(uploaded_by_user_name).trim() !== '') {
    body.uploaded_by_user_name = String(uploaded_by_user_name).trim();
  }

  try {
    const response = await axios.post(ADD_TOOL_TO_LIBRARY, body);
    return response;
  } catch (error) {
    console.log('addToolToLibraryAPI catch', error);
    return { hasError: true, error };
  }
};
